import { FilterInput, SlideFilter } from '@apeiron/common'
import { OrbitalTrackFilterInput } from '@apeiron/orbital'
import { styled } from '@mui/material'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchOrbitalTrackFilterJson from '@src/hooks/json/useFetchOrbitalTrackFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
  }
`

const Form = styled('form')`
  display: flex;
  flex-direction: column;
`

const StyledRangeSlider = styled(RangeSlider)`
  padding: 20px 10px 20px 22px;
`

const CostFilter: FC<Props> = props => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const { data: filterData } = useFetchOrbitalTrackFilterJson()

  const numberOfFilters = useMemo(() => {
    const numberOfFeeFilters =
      R.isNil(filterInput.rentalFeeMax) && R.isNil(filterInput.rentalFeeMin)
        ? 0
        : 1

    const numberOfDurationFilters =
      R.isNil(filterInput.rentalDurationMax) &&
      R.isNil(filterInput.rentalDurationMin)
        ? 0
        : 1

    return R.sum([numberOfFeeFilters, numberOfDurationFilters])
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(
      R.omit(
        [
          'rentalDurationMax',
          'rentalDurationMin',
          'rentalFeeMax',
          'rentalFeeMin'
        ],
        filterInput
      )
    )
  }, [filterInput, onFilterChange])

  const handleOnValueChange = useCallback(
    (filter: FilterInput) => {
      onFilterChange(filter as OrbitalTrackFilterInput)
    },
    [onFilterChange]
  )

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>{t('orbit.list.filter.cost')}</FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        {!R.isNil(filterData) && (
          <Form>
            {R.map((filter: SlideFilter) => {
              return (
                <StyledRangeSlider
                  filterOptions={filterInput}
                  key={filter.name}
                  labelIconURL={filter.image}
                  labelText={filter.name}
                  marks={false}
                  max={filter.max.slot}
                  maxValueKey={filter.max.field}
                  min={filter.min.slot}
                  minValueKey={filter.min.field}
                  remark={filter.remark}
                  step={filter.step}
                  onFilterOptionsChange={handleOnValueChange}
                />
              )
            }, filterData.orbitFilterCost)}
          </Form>
        )}
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: OrbitalTrackFilterInput
  onFilterChange: (input: OrbitalTrackFilterInput) => void
}

export default CostFilter
